import React, { useEffect, useState } from "react";
import ToastMessage from "../../../../common/ToastMessage";
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useAssetCategories from "../../../../store/ReactQueries/assets/assetCategories/useAssetCategories";
import useAddAssCategories from "../../../../store/ReactQueries/assets/assetCategories/useAddAssetCategories";
import useUpdateAssetCategory from "../../../../store/ReactQueries/assets/assetCategories/useUpdateAssetCategory";
import useDeleteAssetCategory from "../../../../store/ReactQueries/assets/assetCategories/useDeleteAssetCategory";
import { GridColDef } from "@mui/x-data-grid";
import { PAGESIZE } from "../../../../utils/constants";
import { FieldErrors } from "../../../../domain/helpers/Errors";
import { formatDate } from "../../../../common/GlobalFunctions";
import { validateSchema } from "../../../../common/ValidateSchema";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { AssetCategoryInterface } from "../../../../models/AssetModel/AssetCategory";
import { assetCategoriesSchema } from "../../../../domain/ValidationSchemas/Asset/assetCategory";

export const AssetCategories = () => {
    const { language } = useLanguageStore();
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [modal, setModal] = useState<{
        add: boolean;
        edit: boolean;
        view: boolean;
    }>({
        add: false,
        edit: false,
        view: false,
    });

    let { data: assetCategory, error } = useAssetCategories(page, pageSize);

    const addAssetCategory = useAddAssCategories(page, pageSize);
    const updateAssetCategory = useUpdateAssetCategory(page, pageSize);
    const deleteAssetCategory = useDeleteAssetCategory(page, pageSize);

    useEffect(() => {
        if (error) ToastMessage.Failed(`Error: ${error.message}`);
    }, [error]);

    const [form, setForm] = useState<AssetCategoryInterface>({
        id: "",
        title: "",
    });

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "Id",
            type: "string",
            editable: false,
            width: 200,
        },
        {
            field: "title",
            headerName: "Title",
            type: "string",
            editable: true,
            width: 250,
        },
        {
            field: "createdDate",
            headerName: "CreatedDate",
            type: "dateTime",
            editable: false,
            width: 150,
            valueFormatter: formatDate,
        },
        {
            field: "updatedDate",
            headerName: "UpdatedDate",
            type: "dateTime",
            editable: false,
            width: 150,
            valueFormatter: formatDate,
        },
    ];

    const addItems = () => {
        setForm({
            ...form,
            title: "",
            id: "",
        });
        setErrors({});
        setModal({ ...modal, add: true });
    };

    const handleError = (error: unknown) => {
        if (error instanceof Error) ToastMessage.Failed(error.message);
        else ToastMessage.Failed();
    };

    const handleSave = async () => {
        const isValid = validateSchema(assetCategoriesSchema, { title: form.title });

        setErrors(isValid.errors);
        if (!isValid.result) return;
        const { id, title } = form;

        const upForm: AssetCategoryInterface = {
            id,
            title,
        };

        if (upForm.id === "") {
            const { id, ...formData } = upForm;
            addAssetCategory.mutate(formData, {
                onSuccess: () => {
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            });
        } else {
            updateAssetCategory.mutate(upForm, {
                onSuccess: () => {
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            });
        }

        setModal({ ...modal, add: false, edit: false, view: false });
    };

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language["ToastDeleteItem"])) {
            deleteAssetCategory.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <CustomDataGrid
            name={language["AssetCategories"]}
            rows={assetCategory || []}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            pageSize={pageSize}
            setForm={setForm}
            setPage={setPage}
            addItems={addItems}
            setModal={setModal}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
            showColumnModal={["title"]}
            hideColumns={["authorId", "id", "tenantId"]}
        />
    );
};
export default AssetCategories;
